import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { showError } from '@/lib/dialogHelper'

import CustomerPicker from '@/widgets/CustomerPicker'
import CustomerCard from '@/widgets/CustomerCard'
import MembershipsService from '@/services/MembershipsService'

import { validateMembership, buildMembershipPayload, createMembershipOnServer } from './submitMembership'
import moment from 'moment'

const getCustomerId = c => String(c?._id ?? c?.customerid ?? c?.id ?? '')
const getCustomerName = c => (c?.name || c?.customerName || `${c?.firstName ?? ''} ${c?.lastName ?? ''}`).trim()

export default function MembershipAddInline() {
    const navigate = useNavigate()
    const [form, setForm] = useState({
        customerId: '',
        customerName: '',
        type: 'basic',
        status: 'active',
        remainingUsage: '10',
        expiryDate: moment().add(1, 'month').format('YYYY-MM-DD'),
    })
    const [customerData, setCustomerData] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)

    const setField = useCallback((name, value) => {
        setForm(prev => ({ ...prev, [name]: value }))
    }, [])

    const handleCustomerSelect = useCallback(
        selectedCustomer => {
            setCustomerData(selectedCustomer)
            setField('customerId', getCustomerId(selectedCustomer))
            setField('customerName', getCustomerName(selectedCustomer))
        },
        [setField]
    )

    const handleCustomerClear = useCallback(() => {
        setCustomerData(null)
        setField('customerId', '')
        setField('customerName', '')
    }, [setField])

    const location = useLocation()
    const autoCustomerId = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return String(
            params.get('customerId') ?? params.get('customerid') ?? params.get('customer_id') ?? params.get('cid') ?? ''
        ).trim()
    }, [location.search])

    useEffect(() => {
        if (!autoCustomerId) return
        if (form.customerId === autoCustomerId) return
        setField('customerId', autoCustomerId)
        setField('customerName', '')
        setCustomerData(null)
    }, [autoCustomerId, form.customerId, setField])

    const handleSubmit = useCallback(
        async e => {
            e?.preventDefault()
            if (submitting) return

            setSubmitting(true)
            setError(null)

            const { ok, errors } = validateMembership(form)
            if (!ok) {
                const lines = ['Please review the form:', ...errors.map(err => `- ${err.message}`)]
                setSubmitting(false)
                showError(lines.join('\n'))
                return
            }

            try {
                const payload = buildMembershipPayload(form)
                const { success, error: apiError } = await createMembershipOnServer(MembershipsService, payload)
                if (!success) throw new Error(apiError || 'Failed to create membership')

                navigate('/memberships', { state: { message: 'Membership created successfully.' } })
            } catch (err) {
                setError(err.message || 'Failed to create membership')
                showError(err.message || 'Failed to create membership')
            } finally {
                setSubmitting(false)
            }
        },
        [form, submitting, navigate]
    )

    return (
        <div className="max-w-4xl mx-auto px-0 sm:px-6 lg:px-8">
            <form onSubmit={handleSubmit} className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
                <div className="px-4 sm:px-6 py-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Create Membership</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Customer: <span className="font-medium text-gray-900"> {form.customerName || customerData?.name || '—'} </span>
                    </p>
                </div>

                {error && (
                    <div className="mx-4 sm:mx-6 mt-6 rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <div className="px-0 sm:px-6 py-6 space-y-6">
                    {/* Customer */}
                    {!form.customerId && <CustomerPicker onCustomerSelect={handleCustomerSelect} />}
                    {form.customerId && (
                        <CustomerCard
                            customerId={form.customerId}
                            handleChange={handleCustomerClear}
                            onLoaded={handleCustomerSelect}
                        />
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 sm:px-0">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                                value={form.type}
                                onChange={e => setField('type', e.target.value)}
                                className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 bg-white">
                                <option value="" disabled>
                                    Select type
                                </option>
                                <option value="basic">Basic</option>
                                <option value="premium">Premium</option>
                                <option value="vip">VIP</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={form.status}
                                onChange={e => setField('status', e.target.value)}
                                className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 bg-white">
                                <option value="" disabled>
                                    Select status
                                </option>
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="expired">Expired</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Remaining usage</label>
                            <input
                                type="number"
                                min={0}
                                value={form.remainingUsage}
                                onChange={e => setField('remainingUsage', e.target.value)}
                                onWheel={e => e.currentTarget.blur()}
                                className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                                placeholder="e.g. 5"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry date</label>
                            <input
                                type="date"
                                value={form.expiryDate}
                                onChange={e => setField('expiryDate', e.target.value)}
                                className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
                            />
                        </div>
                    </div>
                </div>

                <div className="px-4 sm:px-6 py-6 border-t border-gray-200 flex gap-3">
                    <button
                        type="submit"
                        disabled={submitting}
                        className={`px-5 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                        {submitting ? 'Creating…' : 'Create membership'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/memberships')}
                        className="px-5 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    )
}